import React, { useState, useEffect, useCallback } from "react";
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { generatePassphrase } from './passphrase';

const ROLE_LABELS = { 1: "Админ", 2: "Суперадмин", 3: "HR-директор" };
const COMPLEXES = ["Охта", "Кудрово", "Офис"];

// скрываем стрелки-спиннеры у числовых полей
const NO_SPINNER_SX = {
    '& input[type=number]': { MozAppearance: 'textfield' },
    '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
    '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
};
// колесо мыши не должно менять значение
const blurOnWheel = (e) => e.target.blur();

// валидация контактов
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?\d{10,15}$/;
const normalizePhone = (p) => String(p || "").replace(/[\s\-()]/g, "");

const EMPTY_FORM = {
    user_id: "",
    fullname: "",
    username: "",
    email: "",
    phone_number: "",
    complex: "Охта",
    role: 1,
    turnover_plan: 0,
    average_bill_plan: 0,
    visits_number_plan: 0,
    avito: false,
};

export default function AdminsList() {
    const api_url = process.env.REACT_APP_API_URL;
    const role = localStorage.getItem('role');

    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);

    // создание / редактирование администратора
    const [formOpen, setFormOpen] = useState(false);
    const [mode, setMode] = useState("create");   // "create" | "edit"
    const [form, setForm] = useState(EMPTY_FORM);
    const [fieldErrors, setFieldErrors] = useState({});
    const [newPassword, setNewPassword] = useState("");  // только при создании
    const [created, setCreated] = useState(false);       // показ пароля после успешного создания
    const [saving, setSaving] = useState(false);

    // сброс пароля
    const [resetTarget, setResetTarget] = useState(null);  // объект админа или null
    const [resetPassword, setResetPassword] = useState("");
    const [resetDone, setResetDone] = useState(false);

    // уведомления
    const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });
    const showSnack = (severity, message) => setSnack({ open: true, severity, message });
    const closeSnack = (_, reason) => { if (reason !== 'clickaway') setSnack((s) => ({ ...s, open: false })); };

    const getAllAdmins = useCallback(async () => {
        setLoading(true);
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('token') },
            body: JSON.stringify({})
        };
        try {
            const body = await (await fetch(api_url + "/api/allAdmins", requestOptions)).json();
            setAdmins(body);
        } catch (e) {
            showSnack("error", "Не удалось загрузить администраторов");
        } finally {
            setLoading(false);
        }
    }, [api_url]);

    useEffect(() => {
        getAllAdmins();
    }, [getAllAdmins]);

    async function copyToClipboard(text) {
        try {
            await navigator.clipboard.writeText(text);
            showSnack("success", "Скопировано");
        } catch (e) {
            // буфер обмена недоступен — пользователь скопирует вручную
        }
    }

    async function toggleDisabled(user_id, nextDisabled) {
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('token') },
            body: JSON.stringify({ user_id: user_id, is_disabled: nextDisabled })
        };
        const response = await fetch(api_url + "/api/setAdminDisabled", requestOptions);
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            showSnack("error", body.message || "Не удалось изменить статус администратора");
            return;
        }
        getAllAdmins();
    }

    // ---- создание / редактирование ----
    function setField(name, value) {
        setForm((prev) => ({ ...prev, [name]: value }));
        setFieldErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
    }

    function closeForm() {
        setFormOpen(false);
        setCreated(false);
        setFieldErrors({});
    }

    function openCreate() {
        setMode("create");
        setForm(EMPTY_FORM);
        setFieldErrors({});
        setCreated(false);
        setNewPassword(generatePassphrase());
        setFormOpen(true);
    }

    function openEdit(admin) {
        setMode("edit");
        setForm({
            user_id: admin.user_id,
            fullname: admin.fullname || "",
            username: admin.username || "",
            email: admin.email || "",
            phone_number: admin.phone_number || "",
            complex: admin.complex || "Охта",
            role: admin.role,
            turnover_plan: admin.turnover_plan ?? 0,
            average_bill_plan: admin.average_bill_plan ?? 0,
            visits_number_plan: admin.visits_number_plan ?? 0,
            avito: !!admin.avito,
        });
        setFieldErrors({});
        setCreated(false);
        setNewPassword("");
        setFormOpen(true);
    }

    function validateForm() {
        const errs = {};
        if (!form.fullname.trim()) errs.fullname = "Укажите ФИО";
        if (!form.username.trim()) errs.username = "Укажите логин";
        if (!EMAIL_RE.test(form.email.trim())) errs.email = "Введите корректный email";
        if (!PHONE_RE.test(normalizePhone(form.phone_number))) errs.phone_number = "Телефон в формате +7XXXXXXXXXX";
        return errs;
    }

    async function submitForm() {
        const errs = validateForm();
        setFieldErrors(errs);
        if (Object.keys(errs).length > 0) {
            showSnack("error", "Проверьте правильность заполнения полей");
            return;
        }
        if (mode === "create" && !newPassword) {
            showSnack("error", "Сгенерируйте пароль");
            return;
        }
        setSaving(true);

        const endpoint = mode === "create" ? "/api/register" : "/api/updateAdmin";
        const payload = mode === "create" ? { ...form, password: newPassword } : { ...form };

        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('token') },
            body: JSON.stringify(payload)
        };
        let response;
        try {
            response = await fetch(api_url + endpoint, requestOptions);
        } catch (e) {
            setSaving(false);
            showSnack("error", "Нет связи с сервером");
            return;
        }
        setSaving(false);
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            showSnack("error", body.message || "Не удалось сохранить администратора");
            return;
        }
        getAllAdmins();
        if (mode === "create") {
            setCreated(true);   // остаёмся в окне и показываем пароль
        } else {
            showSnack("success", "Изменения сохранены");
            closeForm();
        }
    }

    // ---- сброс пароля ----
    function openReset(admin) {
        setResetTarget(admin);
        setResetPassword(generatePassphrase());
        setResetDone(false);
    }

    async function submitReset() {
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('token') },
            body: JSON.stringify({ user_id: resetTarget.user_id, password: resetPassword })
        };
        const response = await fetch(api_url + "/api/resetAdminPassword", requestOptions);
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            showSnack("error", body.message || "Не удалось сбросить пароль");
            return;
        }
        setResetDone(true);   // пароль остаётся показанным, пока админ не закроет окно
    }

    if (role !== "2") {
        return <Box sx={{ p: 5 }}>Недостаточно прав для просмотра этой страницы.</Box>;
    }

    // варианты роли в форме: 1 и 3; роль 2 показываем, только если редактируем существующего супер-админа
    const roleOptions = form.role === 2 ? [1, 3, 2] : [1, 3];
    // варианты комплекса: стандартные + текущий, если он нестандартный
    const complexOptions = COMPLEXES.includes(form.complex) ? COMPLEXES : [...COMPLEXES, form.complex];

    return (
        <>
            <head>
                <title>Администраторы</title>
                <meta property="og:title" content="Администраторы" key="title" />
            </head>

            <Box sx={{ pt: 5, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h1 style={{ marginLeft: 12 }}>Администраторы</h1>
                <Button variant="contained" sx={{ mr: 2 }} onClick={openCreate}>Создать администратора</Button>
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 640 }}>
                <Table stickyHeader sx={{ minWidth: 650 }} aria-label="admins table">
                    <TableHead>
                        <TableRow>
                            <TableCell>ФИО</TableCell>
                            <TableCell>Комплекс</TableCell>
                            <TableCell>Роль</TableCell>
                            <TableCell align="right">Оборот (план)</TableCell>
                            <TableCell align="right">Ср. чек (план)</TableCell>
                            <TableCell align="right">Машины (план)</TableCell>
                            <TableCell>Статус</TableCell>
                            <TableCell align="right">Действия</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        ) : admins.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                                    Нет администраторов
                                </TableCell>
                            </TableRow>
                        ) : [...admins]
                            .sort((a, b) => (a.is_disabled ? 1 : 0) - (b.is_disabled ? 1 : 0))   // отключённые — в самый низ
                            .map((admin) => {
                            const isDisabled = !!admin.is_disabled;
                            return (
                                <TableRow
                                    key={admin.user_id}
                                    sx={isDisabled ? { opacity: 0.5 } : {}}
                                >
                                    <TableCell>{admin.fullname}</TableCell>
                                    <TableCell>{admin.complex}</TableCell>
                                    <TableCell>{ROLE_LABELS[admin.role] || admin.role}</TableCell>
                                    <TableCell align="right">{admin.turnover_plan ?? 0}</TableCell>
                                    <TableCell align="right">{admin.average_bill_plan ?? 0}</TableCell>
                                    <TableCell align="right">{admin.visits_number_plan ?? 0}</TableCell>
                                    <TableCell>
                                        {isDisabled
                                            ? <Chip label="Отключён" color="default" size="small" />
                                            : <Chip label="Активен" color="success" size="small" />}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                            <Button variant="outlined" size="small" onClick={() => openEdit(admin)}>
                                                Изменить
                                            </Button>
                                            <Button variant="text" size="small" onClick={() => openReset(admin)}>
                                                Сбросить пароль
                                            </Button>
                                            <Switch
                                                checked={!isDisabled}
                                                onChange={(e) => toggleDisabled(admin.user_id, !e.target.checked)}
                                                inputProps={{ 'aria-label': 'admin active toggle' }}
                                            />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* ---- Диалог создания / редактирования администратора ---- */}
            <Dialog open={formOpen} onClose={closeForm} fullWidth maxWidth="sm">
                <DialogTitle>
                    {created ? "Администратор создан" : (mode === "create" ? "Создать администратора" : "Изменить администратора")}
                </DialogTitle>
                <DialogContent>
                    {created ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <Alert severity="success">Администратор <b>{form.fullname}</b> создан</Alert>
                            <TextField label="Пароль" value={newPassword} InputProps={{ readOnly: true }} fullWidth />
                            <Button variant="text" sx={{ alignSelf: 'flex-start', whiteSpace: 'nowrap' }} onClick={() => copyToClipboard(newPassword)}>Копировать</Button>
                            <Box sx={{ color: 'text.secondary', fontSize: 13 }}>
                                Сохраните пароль — посмотреть его снова будет нельзя, только сбросить.
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <TextField label="ФИО" value={form.fullname} onChange={(e) => setField('fullname', e.target.value)} error={!!fieldErrors.fullname} helperText={fieldErrors.fullname || ""} fullWidth />
                            <TextField label="Логин" value={form.username} onChange={(e) => setField('username', e.target.value)} error={!!fieldErrors.username} helperText={fieldErrors.username || ""} fullWidth />
                            <TextField label="Email" type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} error={!!fieldErrors.email} helperText={fieldErrors.email || ""} fullWidth />
                            <TextField label="Телефон" value={form.phone_number} onChange={(e) => setField('phone_number', e.target.value)} error={!!fieldErrors.phone_number} helperText={fieldErrors.phone_number || ""} fullWidth />

                            <FormControl fullWidth>
                                <InputLabel id="complex-label">Комплекс</InputLabel>
                                <Select labelId="complex-label" label="Комплекс" value={form.complex} onChange={(e) => setField('complex', e.target.value)}>
                                    {complexOptions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel id="role-label">Роль</InputLabel>
                                <Select labelId="role-label" label="Роль" value={form.role} onChange={(e) => setField('role', e.target.value)}>
                                    {roleOptions.map((r) => <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>)}
                                </Select>
                            </FormControl>

                            <TextField label="План: оборот" type="number" value={form.turnover_plan} onChange={(e) => setField('turnover_plan', e.target.value)} onWheel={blurOnWheel} sx={NO_SPINNER_SX} fullWidth />
                            <TextField label="План: средний чек" type="number" value={form.average_bill_plan} onChange={(e) => setField('average_bill_plan', e.target.value)} onWheel={blurOnWheel} sx={NO_SPINNER_SX} fullWidth />
                            <TextField label="План: кол-во машин" type="number" value={form.visits_number_plan} onChange={(e) => setField('visits_number_plan', e.target.value)} onWheel={blurOnWheel} sx={NO_SPINNER_SX} fullWidth />

                            <FormControlLabel
                                control={<Checkbox checked={form.avito} onChange={(e) => setField('avito', e.target.checked)} />}
                                label="Авито"
                            />

                            {mode === "create" && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <TextField label="Пароль" value={newPassword} InputProps={{ readOnly: true }} fullWidth />
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button variant="outlined" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }} onClick={() => setNewPassword(generatePassphrase())}>Перегенерировать</Button>
                                        <Button variant="text" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }} onClick={() => copyToClipboard(newPassword)}>Копировать</Button>
                                    </Box>
                                    <Box sx={{ color: 'text.secondary', fontSize: 13 }}>
                                        Сохраните пароль — после создания его нельзя будет посмотреть, только сбросить.
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {created ? (
                        <Button variant="contained" onClick={closeForm}>Закрыть</Button>
                    ) : (
                        <>
                            <Button onClick={closeForm}>Отмена</Button>
                            <Button variant="contained" disabled={saving} onClick={submitForm}>
                                {mode === "create" ? "Создать" : "Сохранить"}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* ---- Диалог сброса пароля ---- */}
            <Dialog open={!!resetTarget} onClose={() => setResetTarget(null)} fullWidth maxWidth="xs">
                <DialogTitle>Сброс пароля</DialogTitle>
                <DialogContent>
                    {resetTarget && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <Box>Администратор: <b>{resetTarget.fullname}</b></Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <TextField label="Новый пароль" value={resetPassword} InputProps={{ readOnly: true }} fullWidth />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {!resetDone && <Button variant="outlined" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }} onClick={() => setResetPassword(generatePassphrase())}>Перегенерировать</Button>}
                                    <Button variant="text" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }} onClick={() => copyToClipboard(resetPassword)}>Копировать</Button>
                                </Box>
                            </Box>
                            <Box sx={{ color: 'text.secondary', fontSize: 13 }}>
                                {resetDone
                                    ? "Пароль обновлён. Сохраните его — посмотреть снова будет нельзя."
                                    : "Нажмите «Применить», чтобы сохранить новый пароль. Старый пароль перестанет действовать."}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetTarget(null)}>{resetDone ? "Закрыть" : "Отмена"}</Button>
                    {!resetDone && <Button variant="contained" onClick={submitReset}>Применить</Button>}
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snack.open}
                autoHideDuration={5000}
                onClose={closeSnack}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={closeSnack} severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
                    {snack.message}
                </Alert>
            </Snackbar>
        </>
    );
}
